import type { KeyboardLayoutId, Niveau4Lexicon, Trin } from '../types';
import { altLayerHint } from '../data/altLayer';
import { trinLabel } from '../lib/curriculum';
import { fingerGuidesForKeys } from '../lib/fingerGuide';
import { useEnterAction } from '../lib/useEnterAction';
import Keyboard from './Keyboard';
import Niveau4LexiconToggle from './Niveau4LexiconToggle';

interface TrinIntroCardProps {
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  unlockedKeys: string[];
  niveau4Lexicon?: Niveau4Lexicon;
  onNiveau4LexiconChange?: (lexicon: Niveau4Lexicon) => void;
  onContinue: () => void;
  onCancel: () => void;
}

export default function TrinIntroCard({
  trin,
  keyboardLayout,
  unlockedKeys,
  niveau4Lexicon,
  onNiveau4LexiconChange,
  onContinue,
  onCancel,
}: TrinIntroCardProps) {
  const guides = fingerGuidesForKeys(trin.newKeys, keyboardLayout);
  const highlightKeys = trin.newKeys.map((k) => (k === ' ' ? ' ' : k.toLowerCase()));
  const showLexiconToggle =
    trin.tierId === 'niveau-4' &&
    niveau4Lexicon !== undefined &&
    onNiveau4LexiconChange !== undefined;

  useEnterAction(onContinue);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 animate-fade-in-up">
      <header>
        <p className="label">{trinLabel(trin)}</p>
        <h1 className="mt-1 text-2xl font-semibold text-strong">{trin.title}</h1>
        <p className="mt-2 text-sm text-muted">{trin.description}</p>
      </header>

      <section className="surface p-5">
        <p className="label">Mål for lektionen</p>
        <p className="mt-2 text-base text-strong">{trin.goal}</p>
      </section>

      {showLexiconToggle && (
        <Niveau4LexiconToggle
          value={niveau4Lexicon}
          onChange={onNiveau4LexiconChange}
        />
      )}

      {trin.newKeys.length === 0 && trin.tierId === 'niveau-5' && trin.trinNumber === 1 && (
        <section className="surface p-5">
          <p className="label">Shift</p>
          <p className="mt-2 text-sm text-strong">
            Hold Shift med lillefingeren på samme side som bogstavet du skriver.
            Venstre Shift til højre hånds bogstaver — højre Shift til venstre.
          </p>
        </section>
      )}

      {trin.allowAltLayer && (
        <section className="surface p-5">
          <p className="label">@ på dit tastatur</p>
          <p className="mt-2 text-sm text-strong">{altLayerHint(keyboardLayout)}</p>
        </section>
      )}

      {guides.length > 0 && (
        <section className="surface p-5">
          <p className="label">Nye taster</p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {guides.map((g) => (
              <li
                key={g.char}
                className="flex items-center gap-3 rounded-xl bg-zinc-50 px-3 py-2 ring-1 ring-zinc-200 dark:bg-zinc-900/50 dark:ring-white/5"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white font-mono text-lg font-semibold text-strong ring-1 ring-zinc-200 dark:bg-zinc-950 dark:ring-white/10">
                  {g.displayChar}
                </span>
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${g.colorClass}`}
                  aria-hidden
                />
                <span className="text-sm text-strong">{g.fingerName}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="overflow-hidden rounded-2xl ring-1 ring-zinc-200 dark:ring-white/5">
        <Keyboard
          layoutId={keyboardLayout}
          unlockedKeys={unlockedKeys}
          highlightedKeys={highlightKeys}
          hintLevel="none"
          showFingerDot
        />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={onContinue}
          className="flex-1 rounded-xl bg-indigo-500 px-4 py-3 font-semibold text-white shadow-sm transition hover:bg-indigo-400 dark:shadow-lg dark:shadow-indigo-500/30"
        >
          Start lektion
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 btn-secondary py-3 font-semibold"
        >
          Annuller
        </button>
      </div>
      <p className="text-center text-xs text-faint">Enter: start lektion</p>
    </div>
  );
}
